import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import { useUpload } from "./UploadContext";
import { toast } from "react-toastify";
import { buildPPFolderName } from "../utils/helpers";

const AutoUploadContext = createContext(null);

const ACCEPTED_EXTENSIONS = /\.(jpg|jpeg|png|gif|webp|bmp|heic|heif|mp4|mov|avi|mkv|wmv|webm|flv|m4v|3gp|ts|mts)$/i;
const VIDEO_EXTS = /\.(mov|avi|mkv|wmv|webm|flv|m4v|3gp|ts|mts|mp4)$/i;

const inferType = (name) => {
  if (VIDEO_EXTS.test(name)) return "video/mp4";
  if (/\.(png)$/i.test(name)) return "image/png";
  if (/\.(gif)$/i.test(name)) return "image/gif";
  if (/\.(webp)$/i.test(name)) return "image/webp";
  return "image/jpeg";
};

const fileKey = (name, size, lastModified) => `${name}__${size}__${lastModified}`;

/* ─── IndexedDB helpers for persisting jobs + directory handles ─── */
const DB_NAME = "AutoUploadDB";
const DB_VERSION = 1;
const STORE_JOBS = "jobs";
const STORE_KEYS = "uploadedKeys";

const openDB = () =>
  new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_JOBS)) db.createObjectStore(STORE_JOBS, { keyPath: "id" });
      if (!db.objectStoreNames.contains(STORE_KEYS)) db.createObjectStore(STORE_KEYS, { keyPath: "jobId" });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

const dbPut = async (storeName, value) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readwrite");
    tx.objectStore(storeName).put(value);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
};

const dbGetAll = async (storeName) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readonly");
    const req = tx.objectStore(storeName).getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
};

const dbDelete = async (storeName, key) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readwrite");
    tx.objectStore(storeName).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
};

/** Save a job to IndexedDB (only serializable fields + dirHandle). */
const persistJob = async (job) => {
  try {
    await dbPut(STORE_JOBS, {
      id: job.id,
      jobName: job.jobName,
      dirHandle: job.dirHandle, // IndexedDB can store FileSystemDirectoryHandle
      folderName: job.folderName,
      gallery: job.gallery,
      album: job.album,
      chatRoomId: job.chatRoomId,
      countryCode: job.countryCode,
      intervalMs: job.intervalMs,
      totalUploaded: job.totalUploaded || 0,
      lastScanTime: job.lastScanTime || null,
      createdAt: job.createdAt,
      sourceType: job.sourceType || "event",
      sourceId: job.sourceId || "",
    });
  } catch (err) {
    console.warn("[AutoUpload] Failed to persist job:", err);
  }
};

const persistKeyState = async (jobId, keysSet, inFlightSet = new Set()) => {
  try {
    await dbPut(STORE_KEYS, {
      jobId,
      keys: Array.from(keysSet),
      inFlightKeys: Array.from(inFlightSet),
    });
  } catch (err) {
    console.warn("[AutoUpload] Failed to persist upload key state:", err);
  }
};

const removePersistedJob = async (jobId) => {
  try {
    await dbDelete(STORE_JOBS, jobId);
    await dbDelete(STORE_KEYS, jobId);
  } catch (err) {
    console.warn("[AutoUpload] Failed to remove persisted job:", err);
  }
};

/**
 * Auto Upload Provider — manages folder watch jobs that periodically scan
 * a local directory for new files and feed them into the UploadContext pipeline.
 */
export const AutoUploadProvider = ({ children }) => {
  const { addSession, startPipeline, sessions } = useUpload();
  const [jobs, setJobs] = useState({}); // { [jobId]: jobState }
  const [loaded, setLoaded] = useState(false); // true after IndexedDB restore
  const jobsRef = useRef({});
  const timersRef = useRef({}); // { [jobId]: intervalId }
  const uploadedKeysRef = useRef({}); // { [jobId]: Set<string> } — confirmed uploaded
  const inFlightKeysRef = useRef({}); // { [jobId]: Set<string> } — queued but not yet confirmed
  const activeSessionIdsRef = useRef({}); // { [jobId]: Set<sessionId> }
  const sessionsRef = useRef({});

  // Keep sessionsRef in sync with sessions state
  useEffect(() => { sessionsRef.current = sessions; }, [sessions]);

  const updateJobs = useCallback((updater) => {
    setJobs((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      jobsRef.current = next;
      return next;
    });
  }, []);

  const patchJob = useCallback((jobId, patch) => {
    updateJobs((prev) => {
      if (!prev[jobId]) return prev;
      return { ...prev, [jobId]: { ...prev[jobId], ...patch } };
    });
  }, [updateJobs]);

  // Warn before browser close when auto-upload is active
  useEffect(() => {
    const handler = (e) => {
      const active = Object.values(jobs).some((j) => j.status === "running");
      if (active) { e.preventDefault(); e.returnValue = ""; }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [jobs]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      Object.values(timersRef.current).forEach(clearInterval);
    };
  }, []);

  // ─── Restore saved jobs from IndexedDB on mount ───
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;

    (async () => {
      try {
        const [savedJobs, allKeys] = await Promise.all([dbGetAll(STORE_JOBS), dbGetAll(STORE_KEYS)]);
        if (!savedJobs || savedJobs.length === 0) { setLoaded(true); return; }

        const keysMap = {};
        const inFlightMap = {};
        (allKeys || []).forEach((entry) => {
          keysMap[entry.jobId] = new Set(entry.keys || []);
          inFlightMap[entry.jobId] = new Set(entry.inFlightKeys || []);
        });

        const restoredJobs = {};
        const autoStartIds = []; // jobs with permission still granted — auto-resume

        for (const saved of savedJobs) {
          // Verify the directory handle is still usable
          let permOk = false;
          try {
            const perm = await saved.dirHandle.queryPermission({ mode: "read" });
            permOk = perm === "granted";
          } catch { /* handle lost */ }

          restoredJobs[saved.id] = {
            id: saved.id,
            jobName: saved.jobName,
            dirHandle: saved.dirHandle,
            folderName: saved.folderName,
            gallery: saved.gallery,
            album: saved.album,
            chatRoomId: saved.chatRoomId,
            countryCode: saved.countryCode,
            intervalMs: saved.intervalMs,
            status: permOk ? "running" : "needs-permission",
            scanning: false,
            statusText: permOk ? "Auto-resumed" : "Needs folder permission — click Resume",
            createdAt: saved.createdAt,
            lastScanTime: saved.lastScanTime || null,
            lastScanNewFiles: 0,
            totalUploaded: saved.totalUploaded || 0,
            sourceType: saved.sourceType || "event",
            sourceId: saved.sourceId || "",
          };

          uploadedKeysRef.current[saved.id] = keysMap[saved.id] || new Set();
          inFlightKeysRef.current[saved.id] = inFlightMap[saved.id] || new Set();

          if (permOk) autoStartIds.push(saved.id);
        }

        jobsRef.current = restoredJobs;
        setJobs(restoredJobs);

        // Auto-start jobs that still have permission
        const autoCount = autoStartIds.length;
        const needsPermCount = Object.keys(restoredJobs).length - autoCount;

        if (autoCount > 0) {
          autoStartIds.forEach((jobId) => {
            // Initial scan
            setTimeout(() => scanAndUpload(jobId), 1000);
            // Periodic scan
            const timer = setInterval(() => scanAndUpload(jobId), restoredJobs[jobId].intervalMs);
            timersRef.current[jobId] = timer;
          });
          toast.success(`Auto-resumed ${autoCount} upload job${autoCount > 1 ? "s" : ""}.`);
        }
        if (needsPermCount > 0) {
          toast.warning(`${needsPermCount} job${needsPermCount > 1 ? "s" : ""} need folder permission — click Resume.`);
        }
      } catch (err) {
        console.warn("[AutoUpload] Failed to restore jobs:", err);
      } finally {
        setLoaded(true);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Scan the directory for new files and upload them.
   */
  const scanAndUpload = useCallback(async (jobId) => {
    const job = jobsRef.current[jobId];
    if (!job || job.status !== "running") return;

    // ── Skip if previous uploads for this job are still active ──
    const activeSIDs = activeSessionIdsRef.current[jobId];
    if (activeSIDs && activeSIDs.size > 0) {
      const currentSessions = sessionsRef.current;
      const ACTIVE_STATUSES = ["pending", "checking", "compressing", "uploading", "retrying"];
      const stillBusy = [...activeSIDs].some((sid) => {
        const s = currentSessions[sid];
        return s && s.started && (s.fileItems || []).some((f) => ACTIVE_STATUSES.includes(f.status));
      });
      if (stillBusy) {
        // Update scan time but don't upload — previous batch still in progress
        patchJob(jobId, {
          lastScanTime: new Date(),
          scanning: false,
          statusText: "Previous uploads still in progress — waiting...",
        });
        const tJob = jobsRef.current[jobId];
        if (tJob) persistJob({ ...tJob, lastScanTime: new Date() });
        return;
      }
      // All done — clean up tracked session IDs
      activeSessionIdsRef.current[jobId] = new Set();
    }

    const { dirHandle, gallery, album, chatRoomId, countryCode } = job;

    // Verify permission still active
    try {
      const perm = await dirHandle.queryPermission({ mode: "read" });
      if (perm !== "granted") {
        const req = await dirHandle.requestPermission({ mode: "read" });
        if (req !== "granted") {
          patchJob(jobId, { status: "paused", statusText: "Permission denied — click Resume to re-grant" });
          clearInterval(timersRef.current[jobId]);
          return;
        }
      }
    } catch {
      patchJob(jobId, { status: "paused", statusText: "Permission error — click Resume to retry" });
      clearInterval(timersRef.current[jobId]);
      return;
    }

    patchJob(jobId, { scanning: true, statusText: "Scanning folder..." });

    try {
      const uploadedKeys = uploadedKeysRef.current[jobId] || new Set();
      const inFlightKeys = inFlightKeysRef.current[jobId] || new Set();
      const newFiles = [];

      // Recursively collect files from directory and all sub-directories
      const scanDir = async (dirH) => {
        for await (const entry of dirH.values()) {
          if (entry.kind === "directory") {
            await scanDir(entry);
          } else if (entry.kind === "file") {
            if (!ACCEPTED_EXTENSIONS.test(entry.name)) continue;
            const file = await entry.getFile();
            const key = fileKey(file.name, file.size, file.lastModified);
            if (!uploadedKeys.has(key) && !inFlightKeys.has(key)) {
              newFiles.push({ file, key });
            }
          }
        }
      };
      await scanDir(dirHandle);

      if (newFiles.length > 0) {
        // Mark as in-flight (memory only — NOT persisted) to prevent double-scanning
        newFiles.forEach(({ key }) => inFlightKeys.add(key));
        inFlightKeysRef.current[jobId] = inFlightKeys;
        persistKeyState(jobId, uploadedKeys, inFlightKeys);
        const batchKeys = newFiles.map(({ key }) => key);

        // Build file items for the upload pipeline
        const fileItems = newFiles.map(({ file }, i) => ({
          id: `auto-${jobId}-${Date.now()}-${i}`,
          originalFile: file,
          processedFile: file,
          fileName: file.name,
          fileSize: file.size,
          fileType: inferType(file.name),
          status: "pending",
          compressProgress: 0,
          uploadProgress: 0,
          compressed: false,
          error: null,
        }));

        const sessionId = `auto-${jobId}-${Date.now()}`;

        // Track this session so future scans know we're busy
        if (!activeSessionIdsRef.current[jobId]) activeSessionIdsRef.current[jobId] = new Set();
        activeSessionIdsRef.current[jobId].add(sessionId);

        addSession({
          id: sessionId,
          album,
          gallery,
          chatRoomId,
          countryCode,
          folderName: buildPPFolderName({
            baseName: gallery?.Name || album?.Name || job.folderName || "Auto Upload",
            date: new Date(),
          }),
          fileItems,
          started: false,
          onFileUploaded: () => {
            // Increment totalUploaded by 1 each time a single file completes.
            // Also persist any files that are already done so refreshes do not rescan them.
            const session = sessionsRef.current[sessionId];
            const currentDoneIds = new Set(
              (session?.fileItems || [])
                .filter((f) => f.status === "done")
                .map((f) => f.id)
            );
            const doneKeys = fileItems
              .map((item, idx) => ({ id: item.id, key: batchKeys[idx] }))
              .filter((x) => currentDoneIds.has(x.id))
              .map((x) => x.key);

            const confirmed = uploadedKeysRef.current[jobId] || new Set();
            const inflight = inFlightKeysRef.current[jobId] || new Set();
            let changed = false;
            doneKeys.forEach((k) => {
              if (!confirmed.has(k)) changed = true;
              confirmed.add(k);
              inflight.delete(k);
            });
            if (changed) persistKeyState(jobId, confirmed, inflight);

            uploadedKeysRef.current[jobId] = confirmed;
            inFlightKeysRef.current[jobId] = inflight;

            const newTotal = (jobsRef.current[jobId]?.totalUploaded || 0) + 1;
            patchJob(jobId, { totalUploaded: newTotal });
            // Persist updated count
            const currentJob = jobsRef.current[jobId];
            if (currentJob) persistJob({ ...currentJob, totalUploaded: newTotal });
          },
          onUploaded: () => {
            // Entire batch done — move successful keys from in-flight to confirmed and persist.
            const confirmed = uploadedKeysRef.current[jobId] || new Set();
            const inflight = inFlightKeysRef.current[jobId] || new Set();
            batchKeys.forEach((k) => { inflight.delete(k); });
            uploadedKeysRef.current[jobId] = confirmed;
            inFlightKeysRef.current[jobId] = inflight;
            persistKeyState(jobId, confirmed, inflight);

            const session = sessionsRef.current[sessionId];
            const doneCount = (session?.fileItems || []).filter((f) => f.status === "done").length;
            patchJob(jobId, { statusText: `Uploaded ${doneCount} file${doneCount > 1 ? "s" : ""} — waiting for next scan` });
          },
        });
        startPipeline(sessionId);

        patchJob(jobId, {
          lastScanTime: new Date(),
          lastScanNewFiles: newFiles.length,
          scanning: false,
          statusText: `Found ${newFiles.length} new file${newFiles.length > 1 ? "s" : ""} — uploading...`,
        });
        // Persist lastScanTime
        const updatedJob = jobsRef.current[jobId];
        if (updatedJob) persistJob({ ...updatedJob, lastScanTime: new Date() });

        toast.info(`Auto Upload: Found ${newFiles.length} new file${newFiles.length > 1 ? "s" : ""} in "${job.folderName}"`);
      } else {
        patchJob(jobId, {
          lastScanTime: new Date(),
          lastScanNewFiles: 0,
          scanning: false,
          statusText: "No new files found",
        });
        // Persist lastScanTime
        const updatedJob2 = jobsRef.current[jobId];
        if (updatedJob2) persistJob({ ...updatedJob2, lastScanTime: new Date() });
      }
    } catch (err) {
      console.error("[AutoUpload] Scan error:", err);
      patchJob(jobId, {
        scanning: false,
        statusText: `Scan error: ${err.message}`,
      });
    }
  }, [addSession, startPipeline, patchJob]);

  /**
   * Create a new auto-upload job.
   */
  const createJob = useCallback(({ jobName, dirHandle, folderName, gallery, album, chatRoomId, countryCode, intervalMs, sourceType, sourceId }) => {
    const jobId = `autojob-${Date.now()}`;

    const parsedCountryCode = Number(countryCode);
    const job = {
      id: jobId,
      jobName: jobName || folderName,
      dirHandle,
      folderName,
      gallery,
      album,
      chatRoomId,
      countryCode: Number.isFinite(parsedCountryCode) ? parsedCountryCode : null,
      intervalMs,
      status: "running",
      scanning: false,
      statusText: "Starting...",
      createdAt: new Date(),
      lastScanTime: null,
      lastScanNewFiles: 0,
      totalUploaded: 0,
      sourceType: sourceType || "event",
      sourceId: sourceId || "",
    };

    uploadedKeysRef.current[jobId] = new Set();

    updateJobs((prev) => ({ ...prev, [jobId]: job }));

    // Persist to IndexedDB
    persistJob(job);

    // Initial scan immediately
    setTimeout(() => scanAndUpload(jobId), 500);

    // Periodic scan
    const timer = setInterval(() => scanAndUpload(jobId), intervalMs);
    timersRef.current[jobId] = timer;

    toast.success(`Auto Upload started for "${folderName}" → ${album.Name}`);
    return jobId;
  }, [updateJobs, scanAndUpload]);

  /**
   * Pause a job (stop timer but keep state).
   */
  const pauseJob = useCallback((jobId) => {
    clearInterval(timersRef.current[jobId]);
    delete timersRef.current[jobId];
    patchJob(jobId, { status: "paused", statusText: "Paused" });
    toast.info("Auto Upload paused.");
  }, [patchJob]);

  /**
   * Resume a paused job.
   */
  const resumeJob = useCallback(async (jobId) => {
    const job = jobsRef.current[jobId];
    if (!job) return;

    // Re-verify/request permission (handles both "paused" and "needs-permission")
    try {
      const perm = await job.dirHandle.requestPermission({ mode: "read" });
      if (perm !== "granted") {
        toast.error("Folder permission denied. Please try again.");
        return;
      }
    } catch {
      toast.error("Could not get folder permission. The folder may no longer be accessible.");
      return;
    }

    patchJob(jobId, { status: "running", statusText: "Resumed — scanning..." });

    // Immediate scan
    setTimeout(() => scanAndUpload(jobId), 300);

    // Restart timer
    const timer = setInterval(() => scanAndUpload(jobId), job.intervalMs);
    timersRef.current[jobId] = timer;

    toast.success("Auto Upload resumed.");
  }, [patchJob, scanAndUpload]);

  /**
   * Stop and remove a job.
   */
  const removeJob = useCallback((jobId) => {
    clearInterval(timersRef.current[jobId]);
    delete timersRef.current[jobId];
    delete uploadedKeysRef.current[jobId];
    updateJobs((prev) => {
      const next = { ...prev };
      delete next[jobId];
      return next;
    });
    removePersistedJob(jobId);
    toast.info("Auto Upload job removed.");
  }, [updateJobs]);

  /**
   * Update an existing job's configuration.
   */
  const updateJob = useCallback(({ jobId, jobName, dirHandle: newDirHandle, folderName: newFolderName, gallery, album, chatRoomId, intervalMs, sourceType, sourceId }) => {
    const job = jobsRef.current[jobId];
    if (!job) return;

    const wasRunning = job.status === "running";
    const intervalChanged = intervalMs && intervalMs !== job.intervalMs;

    // Stop existing timer if interval changed or job was running
    if (wasRunning && intervalChanged) {
      clearInterval(timersRef.current[jobId]);
      delete timersRef.current[jobId];
    }

    const patch = {};
    if (jobName !== undefined) patch.jobName = jobName;
    if (newDirHandle) { patch.dirHandle = newDirHandle; patch.folderName = newFolderName; }
    if (gallery) patch.gallery = gallery;
    if (album) patch.album = album;
    if (chatRoomId !== undefined) patch.chatRoomId = chatRoomId;
    if (intervalMs) patch.intervalMs = intervalMs;
    if (sourceType) patch.sourceType = sourceType;
    if (sourceId !== undefined) patch.sourceId = sourceId;

    patchJob(jobId, patch);

    // Restart timer with new interval if it was running
    if (wasRunning && intervalChanged) {
      const timer = setInterval(() => scanAndUpload(jobId), intervalMs);
      timersRef.current[jobId] = timer;
    }

    // Persist updated job
    setTimeout(() => {
      const updated = jobsRef.current[jobId];
      if (updated) persistJob(updated);
    }, 50);

    toast.success("Job updated.");
  }, [patchJob, scanAndUpload]);

  /**
   * Trigger an immediate scan for a job.
   */
  const scanNow = useCallback((jobId) => {
    const job = jobsRef.current[jobId];
    if (!job || job.scanning) return;
    if (job.status !== "running") return;
    scanAndUpload(jobId);
  }, [scanAndUpload]);

  const activeJobCount = Object.values(jobs).filter((j) => j.status === "running").length;

  return (
    <AutoUploadContext.Provider value={{
      jobs, loaded, createJob, updateJob, pauseJob, resumeJob, removeJob, scanNow, activeJobCount,
    }}>
      {children}
    </AutoUploadContext.Provider>
  );
};

export const useAutoUpload = () => useContext(AutoUploadContext);
